import React, { useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBSpinner } from 'mdb-react-ui-kit'
import { format, parseISO } from 'date-fns'
import { UISelect, UITypeAhead } from '../../../../components/forms/form-fields'
import UniversityStore from '../../store/University.store'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import AdminApi from '../../../../api/admin-api/admin-api'

import './Invitor.component.scss'

/**
 * Invitor Component
 *
 * Allows managers to invite agents from their downline to USABG University sessions.
 * Admins can invite any agent.
 *
 * Features:
 * - Agent search with typeahead (downline for managers, all for admins)
 * - Session selector
 * - Send invitation button
 * - Success/error feedback
 */
const Invitor = observer(() => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [selectedSessionId, setSelectedSessionId] = useState('')
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)
  const [hasDownline, setHasDownline] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isRecruiter, setIsRecruiter] = useState(false)

  // Check if user can invite (has downline or is admin)
  useEffect(() => {
    const checkPermissions = async () => {
      const adminCheck = UserProfileService.isA(['internal-staff', 'internal-admin', 'system-admin'])
      const recruiterCheck = UserProfileService.isA(['recruiter-group'])

      setIsAdmin(adminCheck)
      setIsRecruiter(recruiterCheck)

      if (!adminCheck) {
        // Check if user has downline
        await UniversityStore.fetchDownlineAgents()
        setHasDownline(UniversityStore.downlineAgents.length > 0)
      }
    }

    checkPermissions()
  }, [])

  // Fetch sessions if not loaded
  useEffect(() => {
    if (UniversityStore.sessions.length === 0) {
      UniversityStore.fetchSessions()
    }
  }, [])

  // Usertypes excluded from invitation search
  const excludedUsertypeIds = [35, 36, 219, 222, 226, 227, 228]

  // Search agents based on query
  useEffect(() => {
    const searchAgents = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([])
        return
      }

      setIsSearching(true)

      // Filter out excluded usertypes
      const filterByUsertype = users =>
        (users || []).filter(u => !excludedUsertypeIds.includes(parseInt(u.usertype_id)))

      try {
        if (isRecruiter) {
          // Recruiters can search downline users using /userSearch endpoint
          const response = await AdminApi.getUsersFromSearchTerm(searchQuery, UserProfileService.getUserId())
          setSearchResults(filterByUsertype(response?.data?.data))
        } else if (isAdmin) {
          // Admin can search all users using /userSearch endpoint
          const response = await AdminApi.getUsersFromSearchTerm(searchQuery)
          setSearchResults(filterByUsertype(response?.data?.data))
        } else {
          // Manager searches within downline
          const query = searchQuery.toLowerCase()
          const filtered = UniversityStore.downlineAgents.filter(agent => {
            if (excludedUsertypeIds.includes(parseInt(agent.usertype_id))) {
              return false
            }
            const fullName = (agent.u_fullname || `${agent.u_fname || ''} ${agent.u_lname || ''}`).toLowerCase()
            const email = (agent.u_login || '').toLowerCase()
            return fullName.includes(query) || email.includes(query)
          })
          setSearchResults(filtered.slice(0, 10))
        }
      } catch (ex) {
        console.error('Error searching agents:', ex)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    const debounce = setTimeout(searchAgents, 300)
    return () => clearTimeout(debounce)
  }, [searchQuery, isAdmin, isRecruiter])

  // Memoize available sessions - MUST be before any conditional returns
  const availableSessions = useMemo(() => {
    return UniversityStore.sessions.filter(s => {
      if (s.max_capacity) {
        const enrolled = s.enrolled_count || 0
        if (enrolled >= s.max_capacity) return false
      }
      return true
    })
  }, [UniversityStore.sessions])

  // Don't render if user cannot invite
  const canInvite = isAdmin || isRecruiter || hasDownline
  if (!canInvite && !UniversityStore.isLoading.downline) {
    return null
  }

  const formatDateRange = (startDate, endDate) => {
    try {
      // Extract just YYYY-MM-DD portion and parse as local date to prevent timezone rollover
      const toLocalDate = dateStr => {
        const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
        const [year, month, day] = dateOnly.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = toLocalDate(startDate)
      const end = toLocalDate(endDate)
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`
    } catch {
      return `${startDate} - ${endDate}`
    }
  }

  const getSessionOptionLabel = session => {
    const dateRange = formatDateRange(session.start_date, session.end_date)
    const location = session.location?.name || 'TBD'
    return `${dateRange} - ${location}`
  }

  const getAgentDisplayName = agent => {
    return agent.u_fullname || `${agent.u_fname || ''} ${agent.u_lname || ''}`.trim()
  }

  const handleInvite = async () => {
    if (!selectedAgent) {
      setError('Please select an agent to invite')
      return
    }

    if (!selectedSessionId) {
      setError('Please select a session')
      return
    }

    setError(null)
    setSuccess(null)

    const result = await UniversityStore.inviteAgent(selectedAgent.id, selectedSessionId)

    if (result.success) {
      setSuccess(`Invitation sent to ${getAgentDisplayName(selectedAgent)}`)
      setSelectedAgent(null)
      setSearchQuery('')
      setSelectedSessionId('')
    } else {
      setError(result.error)
    }
  }

  // Loading state
  if (UniversityStore.isLoading.downline) {
    return (
      <MDBCard id='UniversityInvitorComponent'>
        <MDBCardHeader className='invitor-header'>
          <span className='header-title'>
            <i className='fas fa-paper-plane mr-2'></i>
            Send Invitations
          </span>
        </MDBCardHeader>
        <MDBCardBody className='text-center py-5'>
          <MDBSpinner />
          <p className='mt-3'>Checking permissions...</p>
        </MDBCardBody>
      </MDBCard>
    )
  }

  return (
    <MDBCard id='UniversityInvitorComponent'>
      <MDBCardHeader className='invitor-header'>
        <span className='header-title'>
          <i className='fas fa-paper-plane mr-2'></i>
          Send Invitations
        </span>
        {isAdmin && <span className='admin-badge'>Admin</span>}
      </MDBCardHeader>
      <MDBCardBody>
        <div className='invitor-form'>
          <p className='invitor-description'>
            {isAdmin
              ? 'Search for any agent and invite them to attend a USABG University training session.'
              : 'Search for agents in your downline to invite them to a USABG University training session.'}
          </p>

          {/* Agent Search */}
          <div className='form-group'>
            <label>
              <i className='fas fa-user-search mr-2'></i>
              Search Agent
            </label>
            <UITypeAhead
              placeholder={isAdmin ? 'Search by name or email...' : 'Search your downline...'}
              options={searchResults.map(agent => ({
                value: agent.id,
                text: `${getAgentDisplayName(agent)} (${agent.u_email || agent.u_login})`,
                checked: selectedAgent?.id === agent.id,
              }))}
              value={selectedAgent?.id || ''}
              onSelect={option => {
                if (option && option.value) {
                  const agent = searchResults.find(a => a.id === option.value)
                  if (agent) {
                    setSelectedAgent(agent)
                  }
                } else {
                  setSelectedAgent(null)
                }
              }}
              onSearchChange={query => {
                setSearchQuery(query)
                if (!query) setSelectedAgent(null)
              }}
              isSearching={isSearching}
              showDropdownLimit={2}
            />
          </div>

          {/* Selected Agent Display */}
          {selectedAgent && (
            <div className='selected-agent'>
              <i className='fas fa-user-check mr-2'></i>
              <span className='agent-details'>
                <strong>{getAgentDisplayName(selectedAgent)}</strong>
                <small>{selectedAgent.u_email || selectedAgent.u_login}</small>
              </span>
            </div>
          )}

          {/* Session Selector */}
          <div className='form-group'>
            <UISelect
              id='sessionSelect'
              name='session_id'
              label='Select Session'
              value={selectedSessionId}
              options={[
                {
                  value: '',
                  text: '',
                  checked: !selectedSessionId,
                },
                ...availableSessions.map(session => ({
                  value: `${session.id}`,
                  text: `${getSessionOptionLabel(session)}${
                    session.max_capacity
                      ? ` (${session.max_capacity - (session.enrolled_count || 0)} seats available)`
                      : ''
                  }`,
                  checked: `${session.id}` === `${selectedSessionId}`,
                })),
              ]}
              onChange={e => setSelectedSessionId(e.target.value)}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className='alert alert-danger'>
              <i className='fas fa-exclamation-circle mr-2'></i>
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className='alert alert-success'>
              <i className='fas fa-check-circle mr-2'></i>
              {success}
            </div>
          )}

          {/* Invite Button */}
          <div className='invitor-actions'>
            <MDBBtn
              type='button'
              color='success'
              onClick={handleInvite}
              disabled={!selectedAgent || !selectedSessionId || UniversityStore.isLoading.inviting}
            >
              {UniversityStore.isLoading.inviting ? (
                <>
                  <MDBSpinner small className='mr-2' />
                  Sending Invitation...
                </>
              ) : (
                <>
                  <i className='fas fa-paper-plane mr-2'></i>
                  Send Invitation
                </>
              )}
            </MDBBtn>
          </div>

          {/* Info Note */}
          <div className='invitor-info mt-4'>
            <small className='text-muted'>
              <i className='fas fa-info-circle mr-1'></i>
              When an agent receives an invitation, they will need to accept it and pay the deposit ($
              {UniversityStore.depositAmount}) to secure their spot.
            </small>
          </div>
        </div>
      </MDBCardBody>
    </MDBCard>
  )
})

export default Invitor
